const { getElement, sleepUntil } = window.BAO.utils

/*
 ** Use this custom element to inject a style sheet
 ** into the shadow root of another custom element
 */
class StyleSheetInjector extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return [
      'data-custom-element-name',
      'data-style-sheet-url',
      'data-class-names',
      'data-custom-property-names',
      'aria-busy',
    ]
  }

  static get requiredElements () {
    return ['body', 'loading-icon']
  }

  async connectedCallback () {
    super.connectedCallback()

    if (!this.shadowRootParentEl) return

    await sleepUntil(
      () => this.shadowRootParentEl?.shadowRoot?.firstElementChild,
    )
    const firstElementChildEl = this.shadowRootParentEl.shadowRoot
      .firstElementChild

    this.addClassNames(firstElementChildEl)
    this.introduceCustomProperties(firstElementChildEl)
    this.appendStyleSheet()

    if (!this.styleSheetUrl?.length) {
      this.setAttribute('aria-busy', 'false')
    }
  }

  addClassNames (element) {
    if (!element || !this.classNames?.length) return
    this.classNames.forEach(className => {
      element.classList.add(className)
    })
  }

  introduceCustomProperties (element) {
    if (!element || !this.customPropertyNames?.length) return

    const computedStyle = getComputedStyle(this)
    this.customPropertyNames.forEach(customPropertyName => {
      const computedValue = computedStyle.getPropertyValue(customPropertyName)

      element.style.setProperty(customPropertyName, computedValue)
    })
  }

  appendStyleSheet () {
    if (!this.styleSheetUrl?.length) return

    const linkEl = document.createElement('link')
    linkEl.setAttribute('rel', 'stylesheet')
    linkEl.setAttribute('href', this.styleSheetUrl)
    linkEl.setAttribute('media', 'all')
    linkEl.addEventListener('load', () => {
      this.setAttribute('aria-busy', 'false')
    })

    this.shadowRootParentEl.shadowRoot.appendChild(linkEl)
  }

  /* Provide the URL of a stylesheet to inject into the shadowRoot */
  get styleSheetUrl () {
    return this.dataset?.styleSheetUrl
  }

  /*
   ** Provide a comma (,) separated list of class names to be added
   ** to the first element child of the shadowRoot
   */
  get classNames () {
    return this.dataset?.classNames
      ?.split(',')
      ?.filter(className => className.trim().length > 0)
  }

  /*
   ** Provide a comma (,) separated list of custom property names
   ** to make these properties available within the open shadowRoot
   */
  get customPropertyNames () {
    return this.dataset?.customPropertyNames
      ?.split(',')
      ?.filter(customPropertyName => customPropertyName.trim().length > 0)
  }

  /* Provide the name of the custom element */
  get customElementName () {
    return this.dataset?.customElementName
  }

  get shadowRootParentEl () {
    return getElement(this.customElementName, { context: this })
  }
}

if (!window.customElements.get('style-sheet-injector')) {
  window.customElements.define('style-sheet-injector', StyleSheetInjector)
}
